import { formatPhoneLabel, formatTelHref, normalizeDigits } from "@1-now/booking-core";

/**
 * One phone formatter for the footer, the Contact page and the policy pages (1NOW-11109).
 *
 * The package's formatPhoneLabel knows only the US: it printed a UK number as "+442079460958"
 * and a Pakistani one, stored without its "+", as "923095182731". This keeps its US answer
 * and adds the rest.
 *
 * - 10 digits and no "+": a US number, "(352) 910-4078".
 * - 11 digits starting with 1: US or Canada with its country code, "+1 (352) 910-4078".
 * - Any other number of 11 digits or more, or any number with a "+": already international,
 *   "+<country code> <groups>", "+44 20 7946 0958".
 * - Fewer digits, or text: as typed.
 *
 * The country codes are react-phone-input-2's most used ones. That package ships one bundled
 * file with no separate country data to import, so the table below is a copy of the
 * groupings, trimmed to the codes a rental company is likely to carry. A code not in it
 * prints as "+" and the digits in groups of 3 or 4.
 */
const GROUPS: Record<string, number[]> = {
  "7": [3, 3, 2, 2],
  "20": [2, 4, 4],
  "27": [2, 3, 4],
  "31": [1, 8],
  "33": [1, 2, 2, 2, 2],
  "34": [3, 3, 3],
  "39": [3, 3, 4],
  "44": [2, 4, 4],
  "45": [2, 2, 2, 2],
  "46": [2, 3, 2, 2],
  "47": [3, 2, 3],
  "49": [3, 4, 4],
  "52": [2, 4, 4],
  "55": [2, 5, 4],
  "60": [2, 3, 4],
  "61": [3, 3, 3],
  "62": [3, 4, 4],
  "63": [3, 3, 4],
  "64": [2, 3, 4],
  "65": [4, 4],
  "81": [2, 4, 4],
  "86": [3, 4, 4],
  "90": [3, 3, 4],
  "91": [5, 5],
  "92": [3, 7],
  "94": [2, 3, 4],
  "212": [3, 3, 3],
  "234": [3, 3, 4],
  "353": [2, 3, 4],
  "880": [4, 6],
  "965": [4, 4],
  "966": [2, 3, 4],
  "968": [4, 4],
  "971": [2, 3, 4],
  "973": [4, 4],
  "974": [4, 4],
  "977": [3, 3, 4],
};

/** Cut `digits` into groups of the given sizes; what is left over joins the last group. */
const cut = (digits: string, sizes: number[]): string => {
  const out: string[] = [];
  let at = 0;
  sizes.forEach((size, i) => {
    if (at >= digits.length) return;
    const end = i === sizes.length - 1 ? digits.length : at + size;
    out.push(digits.slice(at, end));
    at = end;
  });
  return out.join(" ");
};

/** Groups of 3, then a last group of 3 or 4. */
const loose = (digits: string): string => {
  const out: string[] = [];
  let rest = digits;
  while (rest.length > 4) {
    out.push(rest.slice(0, 3));
    rest = rest.slice(3);
  }
  if (rest) out.push(rest);
  return out.join(" ");
};

const international = (digits: string): string => {
  const code = [3, 2, 1].map((n) => digits.slice(0, n)).find((c) => GROUPS[c]);
  if (!code) return `+${loose(digits)}`;
  const national = digits.slice(code.length);
  // A UK mobile is 7xxx xxxxxx; the table's 2-4-4 is the London shape.
  const sizes = code === "44" && national.startsWith("7") ? [4, 6] : GROUPS[code];
  return `+${code} ${cut(national, sizes)}`;
};

/** The number as a renter reads it. */
export const formatPhone = (raw?: string | null): string => {
  const text = String(raw ?? "").trim().replace(/^tel:/i, "");
  const digits = normalizeDigits(text);
  if (digits.length === 10 && !text.startsWith("+")) return formatPhoneLabel(digits);
  if (digits.length === 11 && digits.startsWith("1")) return formatPhoneLabel(digits);
  if (digits.length >= 11 || (digits.length >= 7 && text.startsWith("+"))) return international(digits);
  return text;
};

/** The tel: link for the number, "" when it has no digits. A 10-digit number without "+" is a US one. */
export const phoneHref = (raw?: string | null): string => {
  const text = String(raw ?? "").trim().replace(/^tel:/i, "");
  // Written with its "+", the number is already in full: "+4733378901" is Norway, not +1 473...
  if (text.startsWith("+")) {
    const digits = normalizeDigits(text);
    return digits ? `tel:+${digits}` : "";
  }
  const dial = formatTelHref(text);
  return dial ? `tel:${dial}` : "";
};
